import { IconBuilding, IconMoodSmile } from "@tabler/icons-react";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Alert, Col, Row, Form, Button } from "react-bootstrap";
import { Controller } from "react-hook-form"; // ✅ ADICIONAR: Import do Controller
import EmojiPickerWrapper from "../../EmojiPIcker";

const StepBusiness = React.memo(
  ({ register, watch, setValue, errors, control }) => {
    // ✅ ADICIONAR: control como prop
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const textareaRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const businessInfo = watch("businessInfo") || "";

    const paymentMethodOptions = [
      "Dinheiro",
      "PIX",
      "Cartão de Débito",
      "Cartão de Crédito",
      "Transferência Bancária",
      "Cheque",
      "Boleto",
    ];

    // ✅ CORRIGIDO: Função para inserir emoji (versão robusta para múltiplos emojis)
    const handleEmojiSelect = useCallback(
      (emoji) => {
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;

          // ✅ CORREÇÃO CRÍTICA: Usar o valor atual do textarea em vez do watch
          // Isso garante que sempre temos o valor mais atualizado
          const currentValue = textarea.value || "";

          // Inserir emoji na posição do cursor
          const newValue =
            currentValue.substring(0, start) +
            emoji +
            currentValue.substring(end);

          // Calcular nova posição do cursor
          const newCursorPosition = start + emoji.length;

          // ✅ CORREÇÃO: Atualizar valor e posição do cursor de forma síncrona
          setValue("businessInfo", newValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });

          // ✅ CORREÇÃO: Usar setTimeout com delay mínimo para melhor timing
          setTimeout(() => {
            if (textareaRef.current) {
              // Garantir que o valor foi atualizado no DOM
              textareaRef.current.value = newValue;
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }
          }, 0);
        }

        // ✅ MANTÉM: NÃO fechar o emoji picker - permite múltiplas seleções
      },
      [setValue] // ✅ CORREÇÃO: Remover businessInfo das dependências
    );

    // Função para alternar o emoji picker
    const toggleEmojiPicker = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowEmojiPicker((prev) => !prev);
    }, []);

    // Fechar emoji picker quando clicar fora
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          showEmojiPicker &&
          emojiPickerRef.current &&
          !emojiPickerRef.current.contains(event.target) &&
          !event.target.closest(".emoji-picker-button")
        ) {
          setShowEmojiPicker(false);
        }
      };

      if (showEmojiPicker) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }, [showEmojiPicker]);

    // Fechar emoji picker quando o componente for desmontado
    useEffect(() => {
      return () => {
        setShowEmojiPicker(false);
      };
    }, []);

    // Função para inserir códigos especiais no final do textarea
    const handleInsertSpecialCode = useCallback(
      (codeType) => {
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          const currentValue = textarea.value || "";
          
          let codeToInsert = "";
          if (codeType === "horario") {
            codeToInsert = "\n\nHorário de atendimento:\n{{horario_atendimento}}";
          } else if (codeType === "tipos") {
            codeToInsert = "\n\nTipos de atendimento:\n{{tipos_atendimento}}";
          }

          // Inserir código no final do textarea
          const newValue = currentValue + codeToInsert;

          // Atualizar formulário
          setValue("businessInfo", newValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });

          // Atualizar posição do cursor no final
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.value = newValue;
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(newValue.length, newValue.length);
            }
          }, 0);
        }
      },
      [setValue]
    );

    return (
      <div>
        <div className="d-flex align-items-center mb-4">
          <IconBuilding className="me-2 text-primary" size={24} />
          <h4 className="mb-0">Sobre o Negócio</h4>
        </div>

        <Form.Group className="mb-4">
          <div className="d-flex justify-content-between mb-1">
            <Form.Label>
              Informações sobre o Negócio * (máximo 1000 caracteres)
            </Form.Label>
            {/* Botão do emoji picker */}
            <Button
              variant="outline-secondary"
              size="sm"
              className="emoji-picker-button p-1"
              onClick={toggleEmojiPicker}
              type="button"
            >
              <IconMoodSmile size={20} />
            </Button>
          </div>

          {/* ✅ SOLUÇÃO PRINCIPAL: Usar Controller em vez de register */}
          <div style={{ position: "relative" }}>
            <div className="d-flex">
              <Controller
                name="businessInfo"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Control
                    {...field} // ✅ Spread das props do field (value, onChange, onBlur, name)
                    as="textarea"
                    rows={10}
                    placeholder="Descreva o que sua empresa faz, como funciona o processo, principais benefícios, diferenciais, horários, tipos de atendimento, formas de pagamento, etc."
                    maxLength={1000}
                    isInvalid={!!errors.businessInfo}
                    ref={(e) => {
                      field.ref(e); // ✅ Ref do react-hook-form
                      textareaRef.current = e; // ✅ Nossa ref para manipulação manual
                    }}
                    style={{
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      resize: "vertical",
                    }}
                  />
                )}
              />
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div
                ref={emojiPickerRef}
                style={{
                  position: "absolute",
                  top: 395,
                  right: 0,
                  zIndex: 1050,
                  marginTop: "5px",
                  
                }}
              >
                <EmojiPickerWrapper
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                />
              </div>
            )}
          </div>

          <Form.Control.Feedback type="invalid">
            {errors.businessInfo?.message}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            {businessInfo?.length || 0}/1000 caracteres. Apenas as informações mais importantes, o restante você poderá treinar seu agente com sua base de conhecimento.
          </Form.Text>
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Endereço</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                {...register("address")}
                placeholder="Rua das Flores, 123 - Centro - São Paulo/SP"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Formas de Pagamento</Form.Label>
              <Row>
                {paymentMethodOptions.map((method) => (
                  <Col md={6} key={method} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`payment-${method}`}
                      label={method}
                      value={method}
                      {...register("paymentMethods")}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </Col>
        </Row>

        <Alert variant="warning">
          <h6>Códigos Especiais:</h6>
          <p className="mb-2">
            Os seguintes códigos se inseridos nas informações serão preenchidos
            automaticamente pelo sistema:
          </p>
          <ul className="mb-3">
            <li className="d-flex align-items-center justify-content-between mb-2">
              <span>
                <code>{"{{horario_atendimento}}"}</code> - Horários de funcionamento
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleInsertSpecialCode("horario")}
                className="ms-2"
              >
                📅 Inserir
              </Button>
            </li>
            <li className="d-flex align-items-center justify-content-between">
              <span>
                <code>{"{{tipos_atendimento}}"}</code> - Tipos de atendimento, duração e valores
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleInsertSpecialCode("tipos")}
                className="ms-2"
              >
                🛠️ Inserir
              </Button>
            </li>
          </ul>
        </Alert>
      </div>
    );
  }
);

StepBusiness.displayName = "StepBusiness";
export default StepBusiness;
